import { createApi } from '@reduxjs/toolkit/query/react';
import { axiosBaseQuery } from 'services/_infra/rtkBaseQuery';
import type {
  Alarm,
  GetAlarmsParams,
  GetAlarmsResult,
} from '../../features/Alarms/types/Alarms.types';

const ALARM_BASE_URL = '/v2/alarm';

export const AlarmsApi = createApi({
  reducerPath: 'AlarmsApi',
  baseQuery: axiosBaseQuery(),
  tagTypes: ['Alarms', 'AlarmsMeta'],
  endpoints: (builder) => ({
    /**
     * Fetches alarm resources that meet criteria.
     *
     * @param {Object} params - Search parameters
     * @param {number} [params.state] - Filter by alarm state (0 for historical, 1 for active)
     * @param {string} [params.search] - Text search (Not implemented)
     * @param {1|0|null|undefined} [params.silenced] - The current silenced state of the alarm. If undefined or null, this parameter is omitted from the request.
     * @param {number} [params.limit=1000] - Query limit [Default: 1000, Max: 50000]
     * @param {number} [params.offset=0] - The query resource offset (how many resources to skip before returning limit)
     * @returns {{
     *   data: any[], // Array of alarms that meet search conditions
     *   meta: {
     *     resultCount: number, // The total number of results that meet the search conditions without limit/offset
     *     nextLink: string | null // A generated next link to get the next page worth of results; null if there is no next page
     *   }
     * }}
     */
    getAlarms: builder.query<GetAlarmsResult, GetAlarmsParams>({
      // undefined fields are to satisfy the type signature of the baseQuery, but are not actually used in the request
      query: (params) => ({
        url: `${ALARM_BASE_URL}`,
        method: 'GET',
        data: undefined,
        params,
        headers: undefined,
        responseType: undefined,
        msAuth: undefined,
      }),
      transformResponse: (response) =>
        response ?? { page: 1, pageSize: 10, totalPages: 0, results: [] }, //fallback to default shape if response is undefined
      providesTags: ['Alarms'],
    }),
    /**
     * Fetches alarm metadata schema for search parameters.
     * @returns {{ searchParams: Array<{ key: string, type: string, default?: number, max?: number }> }}
     */
    getAlarmMeta: builder.query({
      query: () => ({
        url: `${ALARM_BASE_URL}/meta`,
        method: 'GET',
        data: undefined,
        params: undefined,
        headers: undefined,
        responseType: undefined,
        msAuth: undefined,
      }),
      transformResponse: (response) => response?.data?.schema ?? { searchParams: [] },
      providesTags: ['AlarmsMeta'],
    }),
    /**
     * Fetches a single alarm by its numeric id.
     * @param {number} id - Alarm ID
     * @returns {{ data: any[] } | null}
     */
    getAlarmById: builder.query({
      query: (id: number) => ({
        url: `${ALARM_BASE_URL}/${id}`,
        method: 'GET',
        data: undefined,
        params: undefined,
        headers: undefined,
        responseType: undefined,
        msAuth: undefined,
      }),
      transformResponse: (response) => response?.data ?? { data: [] },
    }),
    /**
     * Silences a pending alarm by its numeric id.
     * @param {number} id - Alarm ID
     * @returns {{ data: null, meta: { updated: boolean, reason?: string } }}
     */
    silencePendingAlarm: builder.mutation({
      query: (args: { id: number; silence: boolean }) => ({
        url: `${ALARM_BASE_URL}/pending/${args.id}/silence`,
        method: 'PUT',
        data: { silence: args.silence },
        params: undefined,
        headers: undefined,
        responseType: undefined,
        msAuth: undefined,
      }),
      transformResponse: (response) => response ?? null,
      invalidatesTags: ['Alarms'],
    }),
    /**
     * Fetches metadata for silencing pending alarms.
     *
     * @returns {{
     *   data: {
     *     schema: {
     *       body: Array<{
     *         key: string;
     *         type: string;
     *         default: boolean;
     *       }>;
     *     };
     *   };
     *   meta: Record<string, unknown>;
     * }}
     */
    getSilenceMeta: builder.query({
      query: () => ({
        url: `${ALARM_BASE_URL}/pending/silence/meta`,
        method: 'GET',
        data: undefined,
        params: undefined,
        headers: undefined,
        responseType: undefined,
        msAuth: undefined,
      }),
      transformResponse: (response) => response?.data?.schema ?? { body: [] },
      providesTags: ['AlarmsMeta'],
    }),
    /**
     * Acknowledges a pending alarm by its numeric id.
     * @param {number} id - Alarm ID
     * @returns {{ data: null, meta: { updated: boolean, reason?: string } }}
     */
    acknowledgePendingAlarm: builder.mutation({
      query: (alarmId: number) => ({
        url: `${ALARM_BASE_URL}/pending/${encodeURIComponent(alarmId)}/acknowledge`,
        method: 'PUT',
        data: undefined,
        params: undefined,
        headers: undefined,
        responseType: undefined,
        msAuth: undefined,
      }),
      transformResponse: (response) => response ?? null,
      invalidatesTags: ['Alarms'],
    }),
    /**
     * Share an alarm by id. Expects body: { recipients: string[] }
     * Backend endpoint: POST /v2/alarm/:id/share
     */
    shareAlarm: builder.mutation({
      query: (args: { id: number; recipients: string[] }) => ({
        url: `${ALARM_BASE_URL}/${encodeURIComponent(args.id)}/share`,
        method: 'POST',
        data: { recipients: args.recipients },
        params: undefined,
        headers: undefined,
        responseType: undefined,
        msAuth: undefined,
      }),
      transformResponse: (response) => response ?? null,
    }),
  }),
});

export const {
  useGetAlarmsQuery, // main query for fetching alarms. Has parameters for filtering, pagination, and sorting.
  useGetAlarmMetaQuery, // query for fetching alarm metadata. Does not require any parameters.
  useGetAlarmByIdQuery, // query for fetching a single alarm by its numeric id.
  useSilencePendingAlarmMutation, // mutation for silencing a pending alarm by its numeric id.
  useGetSilenceMetaQuery, // query for fetching silence metadata. Does not require any parameters.
  useAcknowledgePendingAlarmMutation, // mutation for acknowledging a pending alarm by its numeric id.
  useShareAlarmMutation, // mutation for sharing an alarm via email
} = AlarmsApi;
